import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, ToggleLeft, ToggleRight, Tag } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const formatPriceDisplay = (price, trialDays = 0) => {
  const numericPrice = Number(price) || 0;
  if (numericPrice === 0) {
    return trialDays > 0 ? `Free for ${trialDays} Day${trialDays === 1 ? '' : 's'}` : 'Free Plan';
  }
  return `$${numericPrice.toFixed(2)} per Month`;
};

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const storiesPerWeekOptions = ['1','2','3','4','5','6','7','8','9','10','Unlimited'];
  const storyProfilesOptions = Array.from({ length: 8 }, (_, index) => index + 1);
  const storyThemesOptions = ['5', '10', 'Unlimited'];
  const storyFrequencyOptions = ['1x per Week', '2x per Week'];

  const createEmptyPlan = () => ({
    name: '',
    price: 0,
    priceDisplay: '',
    subtitle: '',
    description: '',
    color: 'from-purple-500 to-pink-500',
    badge: '',
    ctaText: 'Select Plan',
    isHighlighted: false,
    isActive: true,
    isPublished: true,
    displayOrder: plans.length + 1,
    trialPeriodDays: 0,
    canSelectLanguage: true,
    canSelectTone: true,
    canSelectVoice: true,
    canDefinePreferences: true,
    storiesPerWeek: '1',
    storyFrequency: '1x per Week',
    storyProfiles: 1,
    storyThemes: '5',
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/plans');
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedPlan(createEmptyPlan());
    setShowAddModal(true);
  };

  const openEditModal = (plan) => {
    setSelectedPlan({
      ...plan,
      priceDisplay: plan.priceDisplay || `$${Number(plan.price || 0).toFixed(2)} per Month`,
      trialPeriodDays: plan.trialPeriodDays ?? 0,
      canSelectLanguage: plan.canSelectLanguage ?? true,
      canSelectTone: plan.canSelectTone ?? true,
      canSelectVoice: plan.canSelectVoice ?? true,
      canDefinePreferences: plan.canDefinePreferences ?? true,
      storiesPerWeek: plan.storiesPerWeek || '1',
      storyFrequency: plan.storyFrequency || '1x per Week',
      storyProfiles: plan.storyProfiles || 1,
      storyThemes: plan.storyThemes || '5',
      isPublished: plan.isPublished ?? plan.isActive ?? true,
    });
    setShowEditModal(true);
  };

  const handleSavePlan = async () => {
    if (!selectedPlan) return;

    if (!selectedPlan.name || selectedPlan.name.trim() === '') {
      toast.error('Please provide a plan name');
      return;
    }

    // Ensure priceDisplay is set (auto-generate if empty)
    if (!selectedPlan.priceDisplay || selectedPlan.priceDisplay.trim() === '') {
      selectedPlan.priceDisplay = formatPriceDisplay(selectedPlan.price || 0, selectedPlan.trialPeriodDays || 0);
    }

    try {
      setSaving(true);
      const payload = {
        name: selectedPlan.name,
        price: Number(selectedPlan.price) || 0,
        priceDisplay: selectedPlan.priceDisplay || formatPriceDisplay(selectedPlan.price, selectedPlan.trialPeriodDays),
        subtitle: selectedPlan.subtitle,
        description: selectedPlan.description,
        color: selectedPlan.color,
        badge: selectedPlan.badge,
        ctaText: selectedPlan.ctaText,
        isHighlighted: selectedPlan.isHighlighted,
        isActive: selectedPlan.isActive,
        isPublished: selectedPlan.isPublished,
        displayOrder: selectedPlan.displayOrder || 0,
        trialPeriodDays: selectedPlan.trialPeriodDays || 0,
        canSelectLanguage: selectedPlan.canSelectLanguage,
        canSelectTone: selectedPlan.canSelectTone,
        canSelectVoice: selectedPlan.canSelectVoice,
        canDefinePreferences: selectedPlan.canDefinePreferences,
        storiesPerWeek: selectedPlan.storiesPerWeek,
        storyFrequency: selectedPlan.storyFrequency,
        storyProfiles: selectedPlan.storyProfiles,
        storyThemes: selectedPlan.storyThemes,
      };

      if (showAddModal) {
        const response = await api.post('/admin/plans', payload);
        if (response.data.success) {
          toast.success('Plan created successfully');
          setShowAddModal(false);
          fetchPlans();
        }
      } else {
        const response = await api.put(`/admin/plans/${selectedPlan.id}`, payload);
        if (response.data.success) {
          toast.success('Plan updated successfully');
          setShowEditModal(false);
          fetchPlans();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async (plan) => {
    try {
      const endpoint = plan.isPublished ? `/admin/plans/${plan.id}/unpublish` : `/admin/plans/${plan.id}/publish`;
      const response = await api.put(endpoint);
      if (response.data.success) {
        toast.success(`Plan ${plan.isPublished ? 'unpublished' : 'published'} successfully`);
        fetchPlans();
      }
    } catch (error) {
      toast.error('Failed to update plan state');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      const response = await api.delete(`/admin/plans/${deleteTarget.id}`);
      if (response.data.success) {
        toast.success('Plan deleted successfully');
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchPlans();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  const filteredPlans = plans.filter((plan) =>
    plan.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminSidebar>
      <div className="space-y-6">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
              Plans Management
            </h1>
            <p className="text-sm sm:text-base text-gray-600">Manage pricing plans for the frontend</p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
          >
            <Plus className="h-5 w-5" />
            Add New Plan
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plans by name..."
              className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base transition-all"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredPlans.length === 0 ? (
          <EmptyState
            icon={<Tag className="h-7 w-7" />}
            title={search ? `No plans found for "${search}"` : 'No plans yet'}
            description={
              search
                ? 'Try adjusting your search terms or clear the filter to see all plans.'
                : 'Create your first pricing plan to get started.'
            }
            action={
              search
                ? {
                    label: 'Clear search',
                    onClick: () => setSearch(''),
                  }
                : {
                    label: 'Add New Plan',
                    onClick: openAddModal,
                  }
            }
          />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Plan Name
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">
                      Features
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredPlans.map((plan) => (
                    <tr
                      key={plan.id}
                      className="hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-pink-50/50 transition-all duration-150 group"
                    >
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12">
                            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md ring-2 ring-white">
                              {plan.name[0]?.toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                            <div className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                              {plan.name}
                              {plan.badge && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                                  {plan.badge}
                                </span>
                              )}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500 truncate">{plan.subtitle}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center text-sm sm:text-base font-semibold text-gray-900">
                          
                          {plan.priceDisplay}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 text-sm font-medium">
                          {plan.features?.length || 0} features
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <button
                          onClick={() => handlePublishToggle(plan)}
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                            plan.isPublished
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-current"></span>
                          {plan.isPublished ? 'Published' : 'Unpublished'}
                        </button>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(plan)}
                            className="p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button>
                          {/* <button
                            onClick={() => openDeleteConfirm(plan)}
                            className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && selectedPlan && (
        <Modal
          title={showAddModal ? 'Add New Plan' : 'Edit Plan'}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setSelectedPlan(null);
          }}
        >
          <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={selectedPlan.name}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="e.g., 14 Day Free Trial"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (USD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={selectedPlan.price}
                  onChange={(e) =>
                    setSelectedPlan((prev) => {
                      const nextPrice = parseFloat(e.target.value) || 0;
                      const wasAuto = prev.priceDisplay === formatPriceDisplay(prev.price, prev.trialPeriodDays);
                      return {
                        ...prev,
                        price: nextPrice,
                        priceDisplay: wasAuto ? formatPriceDisplay(nextPrice, prev.trialPeriodDays) : prev.priceDisplay,
                      };
                    })
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trial Period (days)
                </label>
                <input
                  type="number"
                  min="0"
                  value={selectedPlan.trialPeriodDays}
                  onChange={(e) =>
                    setSelectedPlan((prev) => {
                      const nextTrial = parseInt(e.target.value) || 0;
                      const wasAuto = prev.price === 0 && prev.priceDisplay === formatPriceDisplay(prev.price, prev.trialPeriodDays);
                      return {
                        ...prev,
                        trialPeriodDays: nextTrial,
                        priceDisplay: wasAuto ? formatPriceDisplay(prev.price, nextTrial) : prev.priceDisplay,
                      };
                    })
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedPlan.isPublished ? 'Publish' : 'Unpublish'}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, isPublished: e.target.value === 'Publish' }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                >
                  <option value="Publish">Publish</option>
                  <option value="Unpublish">Unpublish</option>
                </select>
              </div>
            </div>

            <div className="col-span-full flex flex-wrap gap-3 max-sm:flex-col">
              <label className="flex items-start gap-2 whitespace-nowrap text-xs leading-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedPlan.canSelectLanguage}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, canSelectLanguage: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">Select Story Language</span>
              </label>
              <label className="flex items-start gap-2 whitespace-nowrap text-xs leading-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedPlan.canSelectTone}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, canSelectTone: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">Select Story Tone</span>
              </label>
              <label className="flex items-start gap-2 whitespace-nowrap text-xs leading-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedPlan.canSelectVoice}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, canSelectVoice: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">Select Story Voice</span>
              </label>
              <label className="flex items-start gap-2 whitespace-nowrap text-xs leading-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedPlan.canDefinePreferences}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, canDefinePreferences: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">Define Child's Story Preferences</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color Gradient</label>
                <input
                  type="text"
                  value={selectedPlan.color}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, color: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="from-purple-500 to-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
                <input
                  type="text"
                  value={selectedPlan.badge || ''}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, badge: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="e.g., Best Value"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
              <input
                type="text"
                value={selectedPlan.ctaText}
                onChange={(e) => setSelectedPlan((prev) => ({ ...prev, ctaText: e.target.value }))}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="Select"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Story Profiles <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedPlan.storyProfiles}
                  onChange={(e) =>
                    setSelectedPlan((prev) => ({ ...prev, storyProfiles: parseInt(e.target.value) || 1 }))
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                >
                  {storyProfilesOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stories Per Week <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedPlan.storiesPerWeek}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, storiesPerWeek: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                >
                  {storiesPerWeekOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === 'Unlimited' ? 'Unlimited' : option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Story Frequency <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedPlan.storyFrequency}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, storyFrequency: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                >
                  {storyFrequencyOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Story Themes <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedPlan.storyThemes}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, storyThemes: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  required
                >
                  {storyThemesOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === 'Unlimited' ? 'Unlimited' : option}
                    </option>
                  ))}
                </select>
              </div>
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={selectedPlan.displayOrder}
                  onChange={(e) => setSelectedPlan((prev) => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center justify-between pt-6">
                <span className="text-sm font-medium text-gray-700">Highlighted</span>
                <button
                  onClick={() => setSelectedPlan((prev) => ({ ...prev, isHighlighted: !prev.isHighlighted }))}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {selectedPlan.isHighlighted ? (
                    <ToggleRight className="h-6 w-6 text-purple-500" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              Unpublish a plan to hide it from the pricing page. Delete a plan to permanently remove it (soft delete).
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-sm font-medium text-gray-700">Active Status</span>
              <button
                onClick={() => setSelectedPlan((prev) => ({ ...prev, isActive: !prev.isActive }))}
                className="text-gray-400 hover:text-gray-600"
              >
                {selectedPlan.isActive ? (
                  <ToggleRight className="h-6 w-6 text-green-500" />
                ) : (
                  <ToggleLeft className="h-6 w-6 text-gray-400" />
                )}
              </button>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setSelectedPlan(null);
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePlan}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : showAddModal ? 'Create Plan' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && deleteTarget && (
        <ConfirmDialog
          title="Delete Plan"
          description={`Are you sure you want to delete "${deleteTarget.name}"? This action will soft delete the plan (it will be hidden but not permanently removed from the database).`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
        />
      )}

    </AdminSidebar>
  );
};

export default AdminPlans;

